import dayjs from 'dayjs'
import i18next from 'i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FaInfoCircle } from 'react-icons/fa'
import { useDispatch } from 'react-redux'

import { fetchSellerBillingData } from '@/actions/fetch-seller-billing-data'
import Field from '@/components/Form/Field/Field'
import Form from '@/components/Form/Form'
import Button from '@/components/ui/Button/Button'
import { useUser } from '@/contexts/user/User.context'
import { Country } from '@/data/countriesClusters'
import { SellerType, type SellerCompany, type SellerConfig } from '@/network/graphql/types.generated'
import { useAppSelector } from '@/reducers'
import { useReportIssueFromSellerMutation } from '@/views/Contact/operations.generated'

import Link from '../ui/Link/Link'
import Loader from '../ui/Loader/Loader'
import { notificationDanger, notificationSuccess } from '../ui/Notification/Notification'
import Popover from '../ui/Popover/Popover'

import { useSetSellerDac7DataMutation, useValidateSellerDac7DataMutation } from './operations.generated'

import type { ChangeEvent } from 'react'

import './Dac7Form.scss'

type Dac7FormValidity = {
  taxIdentificationNumber: boolean
  countryOfBirth: boolean
  cityOfBirth: boolean
  companyRegistrationNumber: boolean
}

const initialValidity: Dac7FormValidity = {
  taxIdentificationNumber: false,
  countryOfBirth: false,
  cityOfBirth: false,
  companyRegistrationNumber: false,
}

type InputChangeEvent = ChangeEvent<HTMLInputElement>
type SelectChangeEvent = ChangeEvent<HTMLSelectElement>

const zendeskTag = 'seller_dac7'
const zendeskSubject = 'Voggt DAC 7 - Feedback'

export const Dac7Form = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [setSellerDac7Data] = useSetSellerDac7DataMutation()
  const [validateSellerDac7Data] = useValidateSellerDac7DataMutation()
  const [reportIssueFromSeller] = useReportIssueFromSellerMutation()

  const { user, fetchUser, isLoading } = useUser()
  const { sellerConfig, sellerCompany } = user || {}

  const billingData = useAppSelector((state) => state.authentication.billingData)

  useEffect(() => {
    dispatch(fetchSellerBillingData())
  }, [])

  const [validity, setValidity] = useState<Dac7FormValidity>(initialValidity)
  const [loading, setLoading] = useState<boolean>(false)
  const [sellerType, setSellerType] = useState<SellerType | undefined>(sellerConfig?.sellerType ?? undefined)
  const [taxIdentificationNumber, setTaxIdentificationNumber] = useState<string | undefined>(
    sellerConfig?.sellerType === SellerType.Company ? undefined : sellerConfig?.taxIdentificationNumber ?? undefined
  )
  const [countryOfBirth, setCountryOfBirth] = useState<string | undefined>(
    sellerConfig?.sellerType === SellerType.Company ? undefined : sellerConfig?.countryOfBirthIso2Code ?? undefined
  )
  const [cityOfBirth, setCityOfBirth] = useState<string | undefined>(
    sellerConfig?.sellerType === SellerType.Company ? undefined : sellerConfig?.cityOfBirth ?? undefined
  )
  const [companyRegistrationNumber, setCompanyRegistrationNumber] = useState<string | undefined>(
    sellerConfig?.sellerType === SellerType.Company ? sellerCompany?.registrationNumber ?? undefined : undefined
  )
  const [name, setName] = useState<string>('')
  const [birthdate, setBirthdate] = useState<string>('')
  const [address, setAddress] = useState<string>('')
  const [country, setCountry] = useState<string>('')

  useEffect(() => {
    if (!user?.isProfessionalSeller && billingData?.data?.individual) {
      setName(`${billingData.data.individual.first_name} ${billingData.data.individual.last_name}`)
      setBirthdate(
        billingData.data.individual.dob &&
          dayjs()
            .set('date', billingData.data.individual.dob.day)
            .set('month', billingData.data.individual.dob.month - 1)
            .set('year', billingData.data.individual.dob.year)
            .format('YYYY/MM/DD')
      )
      setAddress(getFormattedAddress(billingData.data.individual.address))
      setCountry(billingData.data.individual.address?.country)
    }
    if (user?.isProfessionalSeller && billingData?.data?.company) {
      setName(
        sellerType === SellerType.Company ? billingData.data.company.name : user?.firstName + ' ' + user?.lastName
      )
      setBirthdate((user?.birthdate && dayjs(user?.birthdate).format('YYYY/MM/DD')) || '')
      setAddress(getFormattedAddress(billingData.data.company.address))
      setCountry(billingData.data.company.address?.country)
    }
  }, [billingData, sellerType, user])

  const handleValidityChange = useCallback((name: keyof Dac7FormValidity, isValid: boolean) => {
    setValidity((prev) => ({ ...prev, [name]: isValid }))
  }, [])
  const handleSellerTypeChange = useCallback((e: SelectChangeEvent) => setSellerType(e.target.value as SellerType), [])
  const handleTaxIdentificationNumberChange = useCallback(
    (e: InputChangeEvent) => setTaxIdentificationNumber(e.target.value),
    []
  )
  const handleCountryOfBirthChange = useCallback((e: SelectChangeEvent) => setCountryOfBirth(e.target.value), [])
  const handleCityOfBirthChange = useCallback((e: InputChangeEvent) => setCityOfBirth(e.target.value), [])
  const handleCompanyRegistrationNumberChange = useCallback(
    (e: InputChangeEvent) => setCompanyRegistrationNumber(e.target.value),
    []
  )

  const handleRequestHelp = useCallback(async () => {
    const input = {
      tag: zendeskTag,
      subject: zendeskSubject,
      comment: t('dac7FeedbackComment'),
    }
    await reportIssueFromSeller({
      variables: {
        input,
      },
      onCompleted: () => {
        notificationSuccess(t('successfullySentContact'))
      },
      onError: (error) => {
        notificationDanger(error.message)
      },
    })
  }, [reportIssueFromSeller])

  const handleSubmit = useCallback(async () => {
    if (!user) return

    setLoading(true)
    let input

    if (sellerType === SellerType.Company) {
      input = {
        sellerType,
        registrationNumber: companyRegistrationNumber,
      }
    } else {
      input = {
        sellerType: user.isProfessionalSeller ? SellerType.SelfEmployed : SellerType.Individual,
        taxIdentificationNumber,
        countryOfBirthIso2Code: countryOfBirth,
        cityOfBirth,
      }
    }

    await setSellerDac7Data({
      variables: {
        input,
      },
      onCompleted: () => {
        notificationSuccess(t('successfullySetDac7Data'))
        fetchUser()
      },
      onError: (error) => {
        notificationDanger(error.message)
      },
    })
    setLoading(false)
  }, [
    user,
    sellerType,
    taxIdentificationNumber,
    countryOfBirth,
    cityOfBirth,
    companyRegistrationNumber,
    setSellerDac7Data,
    t,
    fetchUser,
  ])

  const handleValidate = async () => {
    setLoading(true)
    await validateSellerDac7Data({
      onCompleted: () => {
        notificationSuccess(t('successfullyValidatedDac7Data'))
        fetchUser()
      },
      onError: (error) => {
        notificationDanger(error.message)
      },
    })
  }

  const getFormattedAddress = (address?: any): string => {
    if (!address) return ''
    const { line1 = '', postal_code = '', city = '', state = '' } = address
    const addressParts = [line1, postal_code, city, state].filter((part) => part && part.length > 0)
    return addressParts.join(', ')
  }

  const countryOptions = useMemo(
    () => [
      { label: t('dac7CountryNone'), value: '' },
      { label: t('commonFrance'), value: 'FR' },
      { label: t('commonGermany'), value: 'DE' },
      { label: t('commonBelgium'), value: 'BE' },
      { label: t('commonLuxembourg'), value: 'LU' },
      { label: t('commonAustria'), value: 'AT' },
      { label: t('commonNetherlands'), value: 'NL' },
      { label: t('commonGreatBritain'), value: 'GB' },
      { label: t('commonSpain'), value: 'ES' },
      { label: t('commonItaly'), value: 'IT' },
    ],
    [t]
  )

  const sellerTypeOptions = useMemo(
    () => [
      { label: t('dac7InfoSellerTypeNone'), value: '' },
      { label: t('dac7InfoSellerTypeSelfEmployed'), value: SellerType.SelfEmployed },
      { label: t('dac7InfoSellerTypeCompany'), value: SellerType.Company },
    ],
    [t]
  )

  const isValid = useMemo(() => {
    if (!user?.isProfessionalSeller || sellerType === SellerType.SelfEmployed) {
      return validity.taxIdentificationNumber && validity.countryOfBirth && validity.cityOfBirth
    }

    if (user?.isProfessionalSeller && sellerType === SellerType.Company) {
      if (country && ['NL', 'DE', 'AT'].includes(country)) {
        return companyRegistrationNumber && validity.companyRegistrationNumber
      }
      return true
    }

    return false
  }, [validity, user, sellerType, country])

  if (isLoading) return <Loader />
  if (!user || !sellerConfig) return null

  const hasToDisplayField = (field: string): boolean => {
    switch (field) {
      case 'dac7InfoSellerType':
        return user.isProfessionalSeller
      case 'dac7InfoFullName':
      case 'dac7InfoCountry':
      case 'dac7InfoBirthdate':
      case 'dac7InfoAddress':
      case 'dac7InfoTaxIdentificationNumber':
      case 'dac7InfoCountryOfBirth':
      case 'dac7InfoCityOfBirth':
        return !user.isProfessionalSeller || sellerType == SellerType.SelfEmployed
      case 'dac7InfoCompanyName':
      case 'dac7InfoCompanyAddress':
      case 'dac7InfoCompanyCountry':
        return user.isProfessionalSeller && sellerType == SellerType.Company
      case 'dac7InfoCompanyTaxNumber':
      case 'dac7InfoCompanyVatNumber':
        return user.isProfessionalSeller && !!sellerType
      case 'dac7InfoCompanyRegistrationNumber':
        return (
          user.isProfessionalSeller &&
          sellerType == SellerType.Company &&
          !!country &&
          ['NL', 'DE', 'AT'].includes(country)
        )
      default:
        return false
    }
  }

  const isDisabledField = (field: string) => {
    if (!hasToDisplayField(field) || !!sellerConfig.dac7ValidatedAt) {
      return true
    }
    switch (field) {
      case 'dac7InfoSellerType':
        return !!sellerConfig.sellerType
      case 'dac7InfoTaxIdentificationNumber':
        return !!sellerConfig.taxIdentificationNumber
      case 'dac7InfoCountryOfBirth':
        return !!sellerConfig.countryOfBirthIso2Code?.trim()
      case 'dac7InfoCityOfBirth':
        return !!sellerConfig.cityOfBirth?.trim()
      case 'dac7InfoCompanyRegistrationNumber':
        return !!sellerCompany?.registrationNumber?.trim()
      default:
        return false
    }
  }

  const renderFields = () => (
    <>
      {hasToDisplayField('dac7InfoSellerType') && (
        <Field
          className="field-full-width"
          defaultValue={undefined}
          disabled={isDisabledField('dac7InfoSellerType')}
          label={t('dac7InfoSellerType')}
          name="sellerType"
          options={sellerTypeOptions}
          type="select"
          value={sellerType}
          required
          onChange={handleSellerTypeChange}
        />
      )}
      {hasToDisplayField('dac7InfoFullName') && (
        <Field label={t('dac7InfoFullName')} name="fullName" value={name} disabled />
      )}
      {hasToDisplayField('dac7InfoCompanyName') && (
        <Field label={t('dac7InfoCompanyName')} name="companyName" value={sellerCompany?.legalName || ''} disabled />
      )}
      {hasToDisplayField('dac7InfoBirthdate') && (
        <Field label={t('dac7InfoBirthdate')} name="birthdate" value={birthdate} disabled />
      )}
      {hasToDisplayField('dac7InfoCompanyAddress') && (
        <Field label={t('dac7InfoCompanyAddress')} name="companyAddress" value={address || ''} disabled />
      )}
      {hasToDisplayField('dac7InfoAddress') && (
        <Field label={t('dac7InfoAddress')} name="address" value={address || ''} disabled />
      )}
      {hasToDisplayField('dac7InfoCountry') && (
        <Field label={t('dac7InfoCountry')} name="country" value={country} disabled />
      )}
      {hasToDisplayField('dac7InfoCompanyCountry') && (
        <Field label={t('dac7InfoCompanyCountry')} name="companyCountry" value={country} disabled />
      )}
      {hasToDisplayField('dac7InfoCityOfBirth') && (
        <Field
          disabled={isDisabledField('dac7InfoCityOfBirth')}
          label={t('dac7InfoCityOfBirth')}
          name="cityOfBirth"
          value={cityOfBirth}
          required
          onChange={handleCityOfBirthChange}
          onValidityChange={(isValid: boolean) => handleValidityChange('cityOfBirth', isValid)}
        />
      )}
      {hasToDisplayField('dac7InfoCountryOfBirth') && (
        <Field
          disabled={isDisabledField('dac7InfoCityOfBirth')}
          label={t('dac7InfoCountryOfBirth')}
          name="countryOfBirth"
          options={countryOptions}
          type="select"
          value={countryOfBirth}
          required
          onChange={handleCountryOfBirthChange}
          onValidityChange={(isValid: boolean) => handleValidityChange('countryOfBirth', isValid)}
        />
      )}
      {hasToDisplayField('dac7InfoCompanyTaxNumber') && (
        <Field
          className="field-full-width"
          label={t('dac7InfoCompanyTaxNumber')}
          name="companyTaxNumber"
          value={sellerCompany?.taxId || ''}
          disabled
        />
      )}
      {hasToDisplayField('dac7InfoCompanyVatNumber') && (
        <Field
          className="field-full-width"
          label={t('dac7InfoCompanyVatNumber')}
          name="vatNumber"
          value={sellerCompany?.vatId || ''}
          disabled
        />
      )}
      {hasToDisplayField('dac7InfoTaxIdentificationNumber') && (
        <Field
          className="dac7-tin-field field-full-width"
          disabled={isDisabledField('dac7InfoTaxIdentificationNumber')}
          name="taxIdentificationNumber"
          value={taxIdentificationNumber}
          label={
            <Popover
              className="dac7-tin-popover"
              content={
                <>
                  <h2 className="dac7-tin-title-popover">{t('dac7InfoTaxIdentificationNumber')}</h2>
                  <p className="dac7-tin-content-popover">
                    <Trans components={{ '1': <br /> }} i18nKey="dac7TinFieldPopover" />
                  </p>
                </>
              }
            >
              {t('dac7InfoTaxIdentificationNumber')}
              <FaInfoCircle />
            </Popover>
          }
          required
          onChange={handleTaxIdentificationNumberChange}
          onValidityChange={(isValid: boolean) => handleValidityChange('taxIdentificationNumber', isValid)}
        />
      )}
      {hasToDisplayField('dac7InfoCompanyRegistrationNumber') && (
        <Field
          className="field-full-width"
          disabled={isDisabledField('dac7InfoCompanyRegistrationNumber')}
          label={t('dac7InfoCompanyRegistrationNumber')}
          name="companyRegistrationNumber"
          pattern={country === 'DE' ? '^HRB\\d{5}$' : country === 'NL' ? '^[0-9]{8}$' : undefined}
          value={companyRegistrationNumber}
          onChange={handleCompanyRegistrationNumberChange}
          onValidityChange={(isValid: boolean) => handleValidityChange('companyRegistrationNumber', isValid)}
        />
      )}
    </>
  )

  const isAlreadyFilled = (
    sellerConfig:
      | Pick<SellerConfig, 'sellerType' | 'cityOfBirth' | 'countryOfBirthIso2Code' | 'taxIdentificationNumber'>
      | null
      | undefined,
    sellerCompany: Pick<SellerCompany, 'registrationNumber' | 'country'> | null | undefined
  ) => {
    if (!sellerConfig && !sellerCompany) return false
    if (!sellerConfig?.sellerType) return false

    if (sellerType === SellerType.Company) {
      return (
        !!sellerCompany?.registrationNumber ||
        (!!sellerCompany?.country && ![Country.NL, Country.DE, Country.AT].includes(sellerCompany.country))
      )
    }

    return (
      !!sellerConfig?.cityOfBirth && !!sellerConfig?.countryOfBirthIso2Code && !!sellerConfig?.taxIdentificationNumber
    )
  }

  return (
    <Form className="dac7-infos-form" onSubmit={handleSubmit}>
      <div className="dac7-infos-form-fields">{renderFields()}</div>
      <div className="dac7-infos-form-disclaimer">
        <p>{t('dac7Disclaimer_1')}</p>
        <p>{t('dac7Disclaimer_2')}</p>
        {sellerConfig.dac7ValidatedAt && (
          <p>
            {t('dac7Disclaimer_3', {
              date: dayjs(sellerConfig.dac7ValidatedAt)
                .locale(i18next.resolvedLanguage || '')
                .format('DD MMMM YYYY'),
              time: dayjs(sellerConfig.dac7ValidatedAt)
                .locale(i18next.resolvedLanguage || '')
                .format('HH:mm'),
            })}
          </p>
        )}
      </div>
      {!sellerConfig.dac7ValidatedAt && (
        <div className="dac7-infos-form-submit">
          <div className="dac-7-infos-form-submit-left">
            <Link onClick={handleRequestHelp}>{t('requestHelp')}</Link>
          </div>

          <div className="dac7-right-button">
            {isAlreadyFilled(sellerConfig, sellerCompany) ? (
              <Button
                className="validate-action primary"
                isLoading={loading}
                label={t('dac7InfosValidate')}
                onClick={handleValidate}
              />
            ) : (
              <Button
                className="validate-action primary"
                disabled={!isValid}
                isLoading={loading}
                label={t('dac7InfosSubmit')}
                type="submit"
              />
            )}
          </div>
        </div>
      )}
    </Form>
  )
}
